import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Grid, File } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const formatCurrency = (value) => {
    if (typeof value !== 'number') return '-';
    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const FIXED_COLUMNS = [
    { key: 'tenant_name', label: 'Tenant Name' },
    { key: 'property_name', label: 'Property' },
    { key: 'unit_code', label: 'Unit Code' },
    { key: 'tenant_code', label: 'Tenant Code' },
];
const DEFAULT_COLUMNS = FIXED_COLUMNS.map((c) => c.key);

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]
    .map((y) => ({ value: String(y), label: String(y) }));

const initialFilters = {
    year: String(CURRENT_YEAR),
    property_id: '',
    selectedFloor: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const MonthwiseSalesView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions, visibleColumns: visibleFixed,
    } = useColumnConfig(FIXED_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedFloor = ''; next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            year: filters.year,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
            floor: filters.selectedFloor,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getmonthwisesalesreport`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Monthwise report');
        } catch (err) {
            console.error('monthwise report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Monthwise report'));
        } finally {
            setLoading(false);
        }
    };

    // Pivot API rows → one row per tenant with a month→net map.
    const rows = useMemo(() => {
        const grouped = reportData.reduce((acc, item) => {
            const key = `${item.tenant_name}-${item.property_name}-${item.unit_code}-${item.tenant_code}`;
            if (!acc[key]) {
                acc[key] = {
                    tenant_name: item.tenant_name,
                    property_name: item.property_name,
                    unit_code: item.unit_code,
                    tenant_code: item.tenant_code,
                    months: {},
                };
            }
            acc[key].months[item.month] = {
                total_net_sales: item.total_net_sales,
                total_transaction_count: item.total_transaction_count,
            };
            return acc;
        }, {});
        return Object.values(grouped);
    }, [reportData]);

    const sortedData = useMemo(() => {
        const data = [...rows];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [rows, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    // Visible fixed (picker) + 12 month cols + Total.
    const columns = useMemo(() => {
        const monthCols = MONTH_NAMES.map((month) => ({
            key: `month__${month}`,
            label: t(month),
            align: 'right',
            sortable: false,
            render: (item) => (item.months[month] ? Number(item.months[month].total_net_sales).toLocaleString() : '-'),
        }));
        const totalCol = {
            key: '__total',
            label: t('Total'),
            align: 'right',
            sortable: false,
            render: (item) => {
                const sum = MONTH_NAMES.reduce((s, m) => s + (item.months[m]?.total_net_sales || 0), 0);
                return <strong>{sum.toLocaleString()}</strong>;
            },
        };
        return [...visibleFixed, ...monthCols, totalCol];
    }, [visibleFixed, t]);

    const fields = [
        { key: 'year', label: t('Year'), type: 'select', options: YEAR_OPTIONS, placeholder: t('Select year') },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'selectedFloor', label: t('Floor'), type: 'select', options: lookups.floorOptions, placeholder: t('All floors') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    const exportToExcel = () => {
        if (!sortedData.length) return;
        const property_name = nameOf(lookups.propertyOptions, filters.property_id);
        const category_name = nameOf(lookups.categoryOptions, filters.category_id);
        const subcategory_name = nameOf(lookups.subcategoryOptions, filters.subcategory_id);
        const filterDetails = [
            [t('Monthwise Sales Report')],
            [`${t('Year')}: ${filters.year}`],
            property_name && [`${t('Property')}: ${property_name}`],
            category_name && [`${t('Category')}: ${category_name}`],
            subcategory_name && [`${t('Subcategory')}: ${subcategory_name}`],
            filters.selectedFloor && [`${t('Floor')}: ${filters.selectedFloor}`],
            [],
        ].filter(Boolean);

        const tableData = sortedData.map((tenant) => {
            const row = [tenant.tenant_name, tenant.property_name, tenant.unit_code, tenant.tenant_code];
            MONTH_NAMES.forEach((month) => row.push(tenant.months[month]?.total_net_sales || 0));
            const total = MONTH_NAMES.reduce((s, m) => s + (tenant.months[m]?.total_net_sales || 0), 0);
            row.push(formatCurrency(total));
            return row;
        });

        const headerRow = [t('Tenant Name'), t('Property'), t('Unit Code'), t('Tenant Code'), ...MONTH_NAMES.map((m) => t(m)), t('Total')];
        const worksheetData = [...filterDetails, headerRow, ...tableData];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        const startDataRow = filterDetails.length + 1;
        const monthColStart = 4;
        const monthColEnd = 4 + MONTH_NAMES.length;
        for (let R = startDataRow; R < worksheetData.length; R += 1) {
            for (let C = monthColStart; C <= monthColEnd; C += 1) {
                const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
                if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00'; }
            }
        }
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');
        XLSX.writeFile(workbook, 'Monthwise_Sales_Report.xlsx');
        insertToLog(' exported Monthwise report to excel');
    };

    const exportToPDF = () => {
        if (!sortedData.length) return;
        const property_name = nameOf(lookups.propertyOptions, filters.property_id);
        const category_name = nameOf(lookups.categoryOptions, filters.category_id);
        const subcategory_name = nameOf(lookups.subcategoryOptions, filters.subcategory_id);
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        doc.setFontSize(14);
        doc.text(t('Monthwise Sales Report'), 40, 30);
        doc.setFontSize(9);
        let y = 50;
        const line = (txt) => { doc.text(txt, 40, y); y += 13; };
        line(`${t('Year')}: ${filters.year}`);
        if (property_name) line(`${t('Property')}: ${property_name}`);
        if (category_name) line(`${t('Category')}: ${category_name}`);
        if (subcategory_name) line(`${t('Subcategory')}: ${subcategory_name}`);
        if (filters.selectedFloor) line(`${t('Floor')}: ${filters.selectedFloor}`);

        // Short month labels + small font so 17 columns fit landscape without
        // wrapping mid-word / mid-number (full names stay in screen + Excel).
        const columnsHead = [t('Tenant'), t('Property'), t('Unit'), t('Tcode'), ...MONTH_SHORT.map((m) => t(m)), t('Total')];
        const body = sortedData.map((tenant) => {
            const row = [tenant.tenant_name, tenant.property_name, tenant.unit_code, tenant.tenant_code];
            MONTH_NAMES.forEach((month) => row.push(formatCurrency(tenant.months[month]?.total_net_sales || 0)));
            const total = MONTH_NAMES.reduce((s, m) => s + (tenant.months[m]?.total_net_sales || 0), 0);
            row.push(formatCurrency(total));
            return row;
        });
        // Fit the whole table to the page width so nothing clips off the right.
        const MARGIN = 20;
        const usable = doc.internal.pageSize.getWidth() - MARGIN * 2;
        const textW = { 0: 95, 1: 78, 2: 44, 3: 44 };
        const textTotal = Object.values(textW).reduce((s, w) => s + w, 0);
        const numCount = columnsHead.length - 4; // 12 months + Total
        const numW = Math.max(28, (usable - textTotal) / numCount);
        const columnStyles = {
            0: { cellWidth: textW[0] },
            1: { cellWidth: textW[1] },
            2: { cellWidth: textW[2] },
            3: { cellWidth: textW[3] },
        };
        for (let i = 4; i < columnsHead.length; i += 1) columnStyles[i] = { halign: 'right', cellWidth: numW };
        doc.autoTable({
            head: [columnsHead],
            body,
            startY: y + 5,
            margin: { left: MARGIN, right: MARGIN },
            tableWidth: 'wrap',
            styles: { fontSize: 6, cellPadding: 1.2, overflow: 'linebreak', valign: 'middle' },
            headStyles: { fontSize: 6, fillColor: [40, 40, 40], textColor: 255, halign: 'center' },
            theme: 'striped',
            columnStyles,
        });
        doc.save('Monthwise_Sales_Report.pdf');
        insertToLog(' exported Monthwise report to pdf');
    };

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Monthwise Sales Report')}
                subtitle={t('Net sales per tenant across the 12 months of the selected year.')}
                actions={(
                    <Fragment>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToExcel} disabled={!sortedData.length} title={t('Export to Excel')}>
                            <Grid size={14} /> {t('Excel')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToPDF} disabled={!sortedData.length} title={t('Export to PDF')}>
                            <File size={14} /> {t('PDF')}
                        </button>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={FIXED_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={columns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected filters. Try a different year or clearing filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default MonthwiseSalesView;
